import React, { useState } from 'react';
import Seo from '../../seo/Seo';
import { FormConsent } from '../../privacy/ConsentUI';
import { RETENTION } from '../../privacy/privacyConfig';
import axios from 'axios';
import {
  API_BASE_URL,
  LEGAL_ENTITY_NAME,
  REGISTERED_ADDRESS,
  CONTACT_PHONE,
  CONTACT_PHONE_DISPLAY,
  CONTACT_EMAIL,
  GST_NUMBER,
  DUNS_NUMBER,
  DUNS_PROFILE_URL,
} from '../../constants/constants';
import './About.css';
import { COUNTRY_CODES } from '../../constants/countries';
import { Autocomplete, TextField, Box } from '@mui/material';

const AboutAndContact = () => {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', countryCode: '+91', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false); // DPDP consent — never pre-ticked

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const isValid = form.name.trim() && form.email.trim() && form.phone.trim() && form.message.trim() && agreed;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setError('');
    setSuccess(false);
    if (!isValid) return;

    setSending(true);
    try {
      await axios.post(`${API_BASE_URL}/PassThroughRayaServices/contactus`, {
        FullName: form.name.trim() + (form.company ? ` (${form.company.trim()})` : ''),
        EmailAddress: form.email.trim(),
        PhoneNumber: form.phone.trim(),
        CountryCode: form.countryCode,
        Message: form.message.trim(),
      }, {
        headers: { 'X-API-KEY': process.env.REACT_APP_RAYA_SERVICES_API_KEY },
      });
      setSuccess(true);
      setForm({ name: '', company: '', email: '', phone: '', countryCode: '+91', message: '' });
      setAgreed(false);
      setSubmitted(false);
    } catch (err) {
      setError(err.response?.data || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      <Seo path="/contact-us" />
      <div className="contact-container">
        <h1>Get in Touch</h1>
        <p className="contact-intro">
          Have a question about Raya? Need help getting started? We'd love to hear from you.
        </p>

        {success && (
          <div className="contact-success">
            Thank you! Your message has been sent. We'll get back to you soon.
          </div>
        )}
        {error && <div className="contact-error">{error}</div>}

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact-name">Name <span className="required">*</span></label>
              <input
                type="text"
                id="contact-name"
                name="name"
                placeholder="Your name"
                value={form.name}
                onChange={handleChange}
                className={submitted && !form.name.trim() ? 'input-error' : ''}
                maxLength={200}
              />
              {submitted && !form.name.trim() && <span className="field-error">Name is required</span>}
            </div>
            <div className="form-group">
              <label htmlFor="contact-company">Company</label>
              <input
                type="text"
                id="contact-company"
                name="company"
                placeholder="Company name"
                value={form.company}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact-email">Email <span className="required">*</span></label>
              <input
                type="email"
                id="contact-email"
                name="email"
                placeholder="you@company.com"
                value={form.email}
                onChange={handleChange}
                className={submitted && !form.email.trim() ? 'input-error' : ''}
                maxLength={200}
              />
              {submitted && !form.email.trim() && <span className="field-error">Email is required</span>}
            </div>
            <div className="form-group phone-group">
              <label htmlFor="contact-phone">Phone <span className="required">*</span></label>
              <div className="phone-input-wrapper">
                <Autocomplete
                  size="small"
                  options={COUNTRY_CODES}
                  getOptionLabel={(o) => `${o.flag} ${o.label} (${o.code})`}
                  value={COUNTRY_CODES.find(c => c.code === form.countryCode) || null}
                  onChange={(_, v) => setForm({ ...form, countryCode: v?.code || "" })}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      variant="outlined" 
                      placeholder="Code" 
                      sx={{ 
                        width: 130,
                        "& .MuiOutlinedInput-root": {
                          height: 38,
                          borderRadius: "6px",
                          fontSize: "14px",
                          backgroundColor: "#fff"
                        }
                      }} 
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ fontSize: 13 }}>
                      <Box component="span" sx={{ mr: 1, fontSize: 16 }}>{option.flag}</Box>
                      {option.label} ({option.code})
                    </Box>
                  )}
                />
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  placeholder="98765 43210"
                  value={form.phone}
                  onChange={handleChange}
                  className={submitted && !form.phone.trim() ? 'input-error' : ''}
                  maxLength={15}
                  style={{ height: '38px' }}
                />
              </div>
              {submitted && !form.phone.trim() && <span className="field-error">Phone number is required</span>}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="contact-message">Message <span className="required">*</span></label>
            <textarea
              rows={4}
              id="contact-message"
              name="message"
              placeholder="Tell us how we can help..."
              value={form.message}
              onChange={handleChange}
              className={submitted && !form.message.trim() ? 'input-error' : ''}
              maxLength={1000}
            />
            {submitted && !form.message.trim() && <span className="field-error">Message is required</span>}
          </div>
          <FormConsent
            id="contact-consent"
            checked={agreed}
            onChange={setAgreed}
            error={submitted && !agreed}
            purpose="We use your details only to reply to this message and, if you ask, to arrange a demo or quote."
            data="your name, company, email, phone and message"
            retention={RETENTION.enquiry}
          />
          <button type="submit" className="contact-submit" disabled={sending}>
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </form>

        <p className="contact-email">
          Or reach us directly &mdash; call <a href={`tel:${CONTACT_PHONE}`}>{CONTACT_PHONE_DISPLAY}</a>
          {' '}or email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>

        <section className="contact-registered">
          <h2>Registered office</h2>
          <p className="contact-registered-entity">{LEGAL_ENTITY_NAME}</p>
          <p className="contact-registered-address">{REGISTERED_ADDRESS}</p>
          <dl className="contact-registered-ids">
            <div>
              <dt>Phone</dt>
              <dd><a href={`tel:${CONTACT_PHONE}`}>{CONTACT_PHONE_DISPLAY}</a></dd>
            </div>
            <div>
              <dt>GSTIN</dt>
              <dd>{GST_NUMBER}</dd>
            </div>
            <div>
              <dt>D-U-N-S&reg; Number</dt>
              <dd>
                <a href={DUNS_PROFILE_URL} target="_blank" rel="noopener noreferrer">
                  {DUNS_NUMBER}
                </a>
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
  );
};

export default AboutAndContact;
